import { useMemo, useState } from 'react';
import { Tags } from 'lucide-react';
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { isAssisted } from '../logic/records';
import { FONT_BODY, FONT_MONO } from '../theme';
import { Card } from '../ui';
import { EmptyNote, PR_DOT, PillGroup, extraColors } from './common';
import { dayKey, programBands, progressSeries, shortDate } from './statsData';

const METRICS = [
  { id: 'e1rm', label: 'e1RM' },
  { id: 'topWeight', label: 'Top set' },
  { id: 'volume', label: 'Volume' },
];

/* About five session dates spread over the range: a number axis would
 * otherwise tick on round day numbers that mean nothing. */
function pickTicks(series) {
  if (series.length <= 5) return series.map(p => p.x);
  const n = series.length - 1;
  return [...new Set([0, 0.25, 0.5, 0.75, 1].map(f => series[Math.round(f * n)].x))];
}

function longDate(x) {
  return new Date(x * 86400000).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
}

/* Progressive overload for one exercise. The x axis is time (day numbers),
 * not session index, so program blocks can be shaded as real date ranges
 * and a long break shows as a gap. */
export function ProgressCard({ t, isDark, logs, exerciseMeta, programs, events, exercises, selected, onSelect, onOpenNames, duplicates = 0 }) {
  const [metric, setMetric] = useState('e1rm');
  const c = extraColors(isDark);
  // Neighbouring blocks get different hues so a program switch stands out.
  const bandColors = [c.blue, t.accent, c.amber];

  const series = useMemo(
    () => progressSeries(logs, selected, { exerciseMeta, events }),
    [logs, selected, exerciseMeta, events]
  );
  const assisted = selected ? isAssisted(selected, exerciseMeta) : false;
  // Assisted lifts and lifts only done above 12 reps have no e1RM.
  const noE1rm = metric === 'e1rm' && series.length > 0 && !series.some(p => p.e1rm != null);
  const shown = noE1rm ? 'topWeight' : metric;

  const xMin = series.length ? series[0].x : null;
  const xMax = series.length ? series[series.length - 1].x : null;
  // One session still needs a range for the time axis.
  const [d0, d1] = xMin === xMax ? [xMin - 1, xMax + 1] : [xMin, xMax];
  const bands = useMemo(
    () => (xMin === null ? [] : programBands(programs, selected, d0, d1)),
    [programs, selected, xMin, d0, d1]
  );
  const ticks = useMemo(() => pickTicks(series), [series]);

  const values = useMemo(() => series.filter(p => p[shown] != null), [series, shown]);
  const latest = values[values.length - 1];
  let delta = null;
  if (values.length >= 2 && values[0][shown]) {
    const first = values[0][shown];
    const last = latest[shown];
    delta = { abs: Math.round((last - first) * 10) / 10, pct: ((last - first) / first) * 100 };
  }
  // For a counterweight, going down is the progress.
  const better = delta ? (assisted && shown === 'topWeight' ? delta.abs < 0 : delta.abs > 0) : false;
  const worse = delta ? (assisted && shown === 'topWeight' ? delta.abs > 0 : delta.abs < 0) : false;
  const hasPr = series.some(p => p.pr);

  const renderDot = (props) => {
    const { cx, cy, payload, index } = props;
    if (cx == null || cy == null || payload?.[shown] == null) return <g key={`dot-${index}`} />;
    return payload.pr
      ? <circle key={`dot-${index}`} cx={cx} cy={cy} r={5} fill={PR_DOT} stroke={t.cardBg} strokeWidth={1.5} />
      : <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill={t.accent} />;
  };

  return (
    <Card t={t} padding={16}>
      <div className="fit-chart-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Progressive overload</div>
        <PillGroup t={t} label="Chart metric" options={METRICS} value={metric} onChange={setMetric} />
      </div>

      {exercises.length === 0 ? (
        <EmptyNote t={t}>Log a few completed sets to see your progression</EmptyNote>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <select value={selected} aria-label="Exercise"
              onChange={e => onSelect(e.target.value)}
              style={{
                flex: 1, minWidth: 0, minHeight: 44, padding: '10px 12px', borderRadius: 8,
                background: t.inputBg, color: t.text,
                border: `1px solid ${t.inputBorder}`,
                fontSize: 13, fontFamily: FONT_BODY, outline: 'none',
              }}>
              {exercises.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {/* A renamed lift shows up twice in the list above; this is where
                the two histories get merged. */}
            {onOpenNames && (
              <button type="button" onClick={onOpenNames} aria-label="Exercise names" title="Exercise names: merge duplicates" style={{
                minHeight: 44, padding: '0 12px', borderRadius: 8, flexShrink: 0,
                background: 'transparent', color: t.textMuted, border: `1px solid ${t.inputBorder}`,
                fontSize: 12, fontWeight: 600, fontFamily: FONT_BODY, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                <Tags size={15} /> Names
                {duplicates > 0 && (
                  <span style={{ minWidth: 18, height: 18, borderRadius: 999, padding: '0 5px', background: c.amberSoft, color: c.amber, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {duplicates}
                  </span>
                )}
              </button>
            )}
          </div>

          {series.length === 0 ? (
            <EmptyNote t={t}>No completed sets yet for {selected}</EmptyNote>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 6, flexWrap: 'wrap' }}>
                {latest && (
                  <span style={{ fontSize: 22, fontFamily: FONT_MONO, fontWeight: 700, color: t.text }}>
                    {latest[shown].toLocaleString()}
                    <span style={{ fontSize: 12, color: t.textMuted, marginLeft: 4 }}>
                      {shown === 'e1rm' ? 'kg e1RM' : shown === 'topWeight' ? `kg × ${latest.reps}` : 'kg vol'}
                    </span>
                  </span>
                )}
                {delta && (
                  <span style={{
                    fontSize: 12, fontWeight: 700, fontFamily: FONT_MONO,
                    color: better ? '#10b981' : worse ? '#ef4444' : t.textMuted,
                  }}>
                    {delta.abs > 0 ? '+' : ''}{delta.abs.toLocaleString()}
                    {shown !== 'volume' ? 'kg' : ''} ({delta.pct > 0 ? '+' : ''}{delta.pct.toFixed(1)}%)
                    <span style={{ color: t.textFaint, fontWeight: 500, marginLeft: 4 }}>
                      over {values.length} sessions
                    </span>
                  </span>
                )}
              </div>
              {(noE1rm || assisted) && (
                <div style={{ fontSize: 11, color: t.textFaint, marginBottom: 6 }}>
                  {assisted
                    ? 'Assisted: the weight is the counterweight, so lower is better and there is no e1RM.'
                    : 'No e1RM for this lift (sets above 12 reps), showing the top set.'}
                </div>
              )}
              <div style={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={series} margin={{ top: 14, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={t.divider} vertical={false} />
                    {bands.map((b, i) => (
                      <ReferenceArea key={b.id} x1={b.x1} x2={b.x2}
                        fill={bandColors[i % bandColors.length]} fillOpacity={0.12}
                        label={{ value: b.name, position: 'insideTopLeft', fill: t.textMuted, fontSize: 10, offset: 4 }} />
                    ))}
                    {bands.filter(b => b.startsInView).map(b => (
                      <ReferenceLine key={`start-${b.id}`} x={b.x1} stroke={t.textMuted} strokeDasharray="4 3" />
                    ))}
                    <XAxis dataKey="x" type="number" domain={[d0, d1]} ticks={ticks} allowDecimals={false}
                      tickFormatter={x => shortDate(dayKey(x))}
                      tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} />
                    <YAxis tick={{ fill: t.textFaint, fontSize: 11 }} stroke={t.divider} domain={['auto', 'auto']} width={40} />
                    <Tooltip
                      contentStyle={{ background: t.cardBg, border: `1px solid ${t.cardBorder}`, borderRadius: 8, fontSize: 12 }}
                      labelStyle={{ color: t.text }}
                      itemStyle={{ color: t.text }}
                      labelFormatter={x => longDate(x)}
                      formatter={(value, _key, item) => {
                        const p = item?.payload || {};
                        const pr = p.pr ? '  · PR' : '';
                        if (shown === 'e1rm') return [`${value} kg (${p.topWeight}kg × ${p.reps})${pr}`, 'e1RM'];
                        if (shown === 'topWeight') return [`${value}kg × ${p.reps}${pr}`, 'Top set'];
                        return [`${Number(value).toLocaleString()}kg${pr}`, 'Volume'];
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey={shown}
                      stroke={t.accent}
                      strokeWidth={2}
                      connectNulls
                      dot={renderDot}
                      activeDot={{ r: 5, fill: t.accent, stroke: t.cardBg, strokeWidth: 2 }}
                      isAnimationActive={true}
                      animationDuration={500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {(hasPr || bands.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginTop: 8, fontSize: 11, color: t.textFaint }}>
                  {hasPr && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: PR_DOT, display: 'inline-block' }} />
                      PR session
                    </span>
                  )}
                  {bands.map((b, i) => (
                    <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 12, height: 9, borderRadius: 2, background: bandColors[i % bandColors.length], opacity: 0.45, display: 'inline-block' }} />
                      {b.name}: {shortDate(b.startDate)}{b.endDate ? ` – ${shortDate(b.endDate)}` : ', running'}
                    </span>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
}
